/**
 * ============================================================================
 * DS CART UPSELL — <ds-cart-upsell> (Empfehlungs-Modus)
 * ============================================================================
 *
 * Rendert den Upsell-Slider im DS Cart Drawer über die Shopify
 * Product-Recommendations-API. Rein JS-getrieben nach dem Muster von
 * <ds-selectable-gifts>: das Element liegt AUSSERHALB der
 * data-ajax-cart-section-Regionen, überlebt dadurch LAC-Re-Renders und
 * synchronisiert sich über liquid-ajax-cart:init / :request-end mit dem
 * Warenkorb-State (window.liquidAjaxCart.cart).
 *
 * - Empfehlungen werden pro Anker-Produkt gecacht und nur bei Anker-Wechsel neu
 *   geladen; bereits im Warenkorb liegende Produkte werden bei jeder
 *   Cart-Änderung neu herausgefiltert. Anker = bezahlter Artikel, Richtung aus
 *   data-anchor (last_added = Default / first_in_cart, siehe anchorProductId()),
 *   `intent` kommt aus data-intent
 *   (related | complementary = in Search & Discovery gepflegte Produkte).
 * - Add-to-Cart läuft über die dokumentierte LAC-Requests-API
 *   (window.liquidAjaxCart.add) via delegiertem Klick auf [data-ds-upsell-add]
 *   — bewusst KEINE /cart/add-Formulare: LAC bindet Submit-Listener pro
 *   Formular beim Init, dynamisch eingefügte Formulare (JS-Karten,
 *   LAC-Re-Renders) würden nativ submitten und auf /cart weiterleiten.
 *   liquidAjaxCart.add() durchläuft den LAC-Standard-Pfad (request-end,
 *   Section-Re-Render, Kampagnen-/Geschenk-Mutations).
 *
 * Der Modus „Ausgewählte Produkte" (manuelle product_list) rendert dagegen
 * serverseitig in snippets/ds-cart-upsell.liquid (LAC-Section) und nutzt
 * denselben delegierten Add-Handler aus dieser Datei.
 * Doku: docs/ds-cart-drawer.md.
 */

(function () {
    if (customElements.get('ds-cart-upsell')) return;

    // Delegierter Add-Handler für beide Modi (Empfehlungen + manuelle Liste).
    // Überlebt LAC-Re-Renders, da auf document statt auf den Karten gebunden.
    document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ds-upsell-add]');
        if (!button || button.disabled) return;

        const variantId = Number(button.dataset.variantId);
        if (!variantId || !window.liquidAjaxCart) return;

        button.disabled = true;
        window.liquidAjaxCart.add(
            { items: [{ id: variantId, quantity: 1 }] },
            {
                lastCallback: () => {
                    // Erfolg: Karte verschwindet beim Re-Render ohnehin.
                    // Fehler: Button wieder freigeben.
                    button.disabled = false;
                },
            }
        );
    });

    const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

    /**
     * Shopify-Money-Formatter für Cent-Beträge aus den JSON-Endpoints
     * (Pendant zum Liquid-`money`-Filter, gespeist aus shop.money_format).
     */
    function formatMoney(cents, format) {
        const formatString = format || '{{ amount }}';
        const placeholder = formatString.match(MONEY_PLACEHOLDER);

        function formatWithDelimiters(number, precision, thousands, decimal) {
            if (isNaN(number) || number == null) return '0';
            const fixed = (number / 100).toFixed(precision);
            const parts = fixed.split('.');
            const units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
            return parts[1] ? units + decimal + parts[1] : units;
        }

        let value;
        switch (placeholder && placeholder[1]) {
            case 'amount_no_decimals':
                value = formatWithDelimiters(cents, 0, ',', '.');
                break;
            case 'amount_with_comma_separator':
                value = formatWithDelimiters(cents, 2, '.', ',');
                break;
            case 'amount_no_decimals_with_comma_separator':
                value = formatWithDelimiters(cents, 0, '.', ',');
                break;
            case 'amount_with_apostrophe_separator':
                value = formatWithDelimiters(cents, 2, "'", '.');
                break;
            default:
                value = formatWithDelimiters(cents, 2, ',', '.');
        }

        return formatString.replace(MONEY_PLACEHOLDER, value);
    }

    function escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, (char) => {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
        });
    }

    class DSCartUpsell extends HTMLElement {
        constructor() {
            super();
            this.recommendedProducts = null; // Cache der API-Antwort für recommendedForId
            this.recommendedForId = null; // Anker-Produkt-ID, für die der Cache gilt
            this.syncToken = 0; // verwirft veraltete (überholte) Sync-Läufe
        }

        connectedCallback() {
            // WICHTIG: hier nicht auf Kind-Elemente zugreifen — beim Parser-Upgrade
            // (Script lädt vor dem Element) sind die Kinder noch nicht geparst.
            // Der Scroller wird deshalb lazy in render() abgefragt; die LAC-Events
            // feuern ohnehin erst nach dem Parsen (LAC ist ein deferred Module).
            this.abortController = new AbortController();
            const signal = this.abortController.signal;

            document.addEventListener('liquid-ajax-cart:request-end', () => this.sync(), { signal });

            if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
                this.sync();
            } else {
                document.addEventListener('liquid-ajax-cart:init', () => this.sync(), { once: true, signal });
            }
        }

        disconnectedCallback() {
            this.abortController.abort();
        }

        async sync() {
            const cart = window.liquidAjaxCart && window.liquidAjaxCart.cart;
            if (!cart) return;

            if (cart.item_count === 0) {
                this.hidden = true;
                return;
            }

            const token = ++this.syncToken;
            const anchorProductId = this.anchorProductId(cart);
            if (!anchorProductId) {
                this.hidden = true;
                return;
            }

            if (this.recommendedForId !== anchorProductId) {
                let products;
                try {
                    products = await this.fetchRecommendations(anchorProductId);
                } catch (error) {
                    this.hidden = true;
                    return;
                }
                if (token !== this.syncToken) return;
                this.recommendedProducts = products;
                this.recommendedForId = anchorProductId;
            }

            const inCart = new Set(cart.items.map((item) => item.product_id));
            const products = this.recommendedProducts.filter((product) => !inCart.has(product.id));

            if (products.length === 0) {
                this.hidden = true;
                return;
            }

            const scroller = this.querySelector('[data-upsell-scroller]');
            if (!scroller) return;
            scroller.innerHTML = products.map((product) => this.cardHtml(product)).join('');
            this.hidden = false;
        }

        /**
         * Anker-Produkt = bezahlter Warenkorb-Artikel, aus dem die Empfehlungen
         * abgeleitet werden. Shopify liefert cart.items mit dem jüngsten Artikel
         * zuerst, `data-anchor` steuert die Richtung: 'first_in_cart' = zuerst hinzugefügter Artikel (Empfehlung bleibt
         * stehen, wenn weitere Artikel dazukommen), sonst 'last_added' (Default) =
         * jüngster Artikel. Geschenk-/Gratis-Positionen sind kein Kaufkontext und
         * werden übersprungen — identische Regel wie im Metafeld-Modus
         * (snippets/ds-cart-upsell.liquid).
         */
        anchorProductId(cart) {
            const items =
                this.dataset.anchor === 'first_in_cart' ? [...cart.items].reverse() : cart.items;
            const anchor = items.find((item) => item.final_price > 0);
            return anchor ? anchor.product_id : null;
        }

        async fetchRecommendations(productId) {
            const params = new URLSearchParams({
                product_id: productId,
                limit: this.dataset.limit || '6',
                intent: this.dataset.intent || 'related',
            });
            const response = await fetch(`${this.dataset.recommendationsUrl}.json?${params}`);
            if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);
            const data = await response.json();
            return data.products || [];
        }

        cardHtml(product) {
            const variant = product.variants.find((v) => v.available) || product.variants[0];
            const title = escapeHtml(product.title);
            const price = formatMoney(variant.price, this.dataset.moneyFormat);
            // Struktur-Pendant zu snippets/ds-cart-upsell-card.liquid — dort spiegeln.
            const compareAt =
                variant.compare_at_price > variant.price
                    ? `<s class="ds-cart-upsell__price-compare">${formatMoney(variant.compare_at_price, this.dataset.moneyFormat)}</s>`
                    : '';
            const imageUrl = product.featured_image
                ? product.featured_image + (product.featured_image.includes('?') ? '&' : '?') + 'width=128'
                : '';

            // Leeres aria-label würde den Accessible Name löschen → Attribut nur mit Inhalt.
            const ariaLabel = this.dataset.addAriaLabel
                ? ` aria-label="${escapeHtml(this.dataset.addAriaLabel.replace('[title]', product.title))}"`
                : '';

            const action = variant.available
                ? `<button type="button" class="ds-cart-upsell__add-btn" data-ds-upsell-add data-variant-id="${variant.id}"${ariaLabel}>${escapeHtml(this.dataset.addLabel)}</button>`
                : `<span class="ds-cart-upsell__sold-out">${escapeHtml(this.dataset.soldOutLabel)}</span>`;

            return `
                <div class="ds-cart-upsell__item">
                    <div class="ds-cart-upsell__image-wrap">
                        ${imageUrl ? `<img class="ds-cart-upsell__image" src="${escapeHtml(imageUrl)}" alt="${title}" width="64" height="64" loading="lazy">` : ''}
                    </div>
                    <div class="ds-cart-upsell__info">
                        <div class="ds-cart-upsell__meta">
                            <a href="${escapeHtml(product.url)}" class="ds-cart-upsell__name">${title}</a>
                            <p class="ds-cart-upsell__price">${compareAt}<span class="ds-cart-upsell__price-current">${price}</span></p>
                        </div>
                        ${action}
                    </div>
                </div>`;
        }
    }

    customElements.define('ds-cart-upsell', DSCartUpsell);
})();
